/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import ApplicationUserAnnouncementTimeslotEntity from 'Models/Security/Acl/ApplicationUserAnnouncementTimeslotEntity';
import VisitorsAnnouncementTimeslotEntity from 'Models/Security/Acl/VisitorsAnnouncementTimeslotEntity';
import AgencyPlaylisterAnnouncementTimeslotEntity from 'Models/Security/Acl/AgencyPlaylisterAnnouncementTimeslotEntity';
import AgencyAdminAnnouncementTimeslotEntity from 'Models/Security/Acl/AgencyAdminAnnouncementTimeslotEntity';
import MustardAdminAnnouncementTimeslotEntity from 'Models/Security/Acl/MustardAdminAnnouncementTimeslotEntity';
import MustardManagementAnnouncementTimeslotEntity from 'Models/Security/Acl/MustardManagementAnnouncementTimeslotEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IAnnouncementTimeslotEntityAttributes extends IModelAttributes {
	start: Date;

	announcementId?: string;
	announcement?: Models.AnnouncementEntity | Models.IAnnouncementEntityAttributes;
	scheduleId?: string;
	schedule?: Models.ScheduleEntity | Models.IScheduleEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('AnnouncementTimeslotEntity', 'Announcement Timeslot')
// % protected region % [Customise your entity metadata here] end
export default class AnnouncementTimeslotEntity extends Model
	implements IAnnouncementTimeslotEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new ApplicationUserAnnouncementTimeslotEntity(),
		new VisitorsAnnouncementTimeslotEntity(),
		new AgencyPlaylisterAnnouncementTimeslotEntity(),
		new AgencyAdminAnnouncementTimeslotEntity(),
		new MustardAdminAnnouncementTimeslotEntity(),
		new MustardManagementAnnouncementTimeslotEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Start'] off begin
	@observable
	@attribute<AnnouncementTimeslotEntity, Date>()
	@CRUD({
		name: 'Start',
		displayType: 'datetimepicker',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public start: Date;
	// % protected region % [Modify props to the crud options here for attribute 'Start'] end

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Announcement'] off begin
		name: 'Announcement',
		displayType: 'reference-combobox',
		order: 20,
		referenceTypeFunc: () => Models.AnnouncementEntity,
		// % protected region % [Modify props to the crud options here for reference 'Announcement'] end
	})
	public announcementId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public announcement: Models.AnnouncementEntity;

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Schedule'] off begin
		name: 'Schedule',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.ScheduleEntity,
		// % protected region % [Modify props to the crud options here for reference 'Schedule'] end
	})
	public scheduleId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public schedule: Models.ScheduleEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAnnouncementTimeslotEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAnnouncementTimeslotEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.start !== undefined) {
				if (attributes.start === null) {
					this.start = attributes.start;
				} else {
					this.start = moment(attributes.start).toDate();
				}
			}
			if (attributes.announcementId !== undefined) {
				this.announcementId = attributes.announcementId;
			}
			if (attributes.announcement !== undefined) {
				if (attributes.announcement === null) {
					this.announcement = attributes.announcement;
				} else if (attributes.announcement instanceof Models.AnnouncementEntity) {
					this.announcement = attributes.announcement;
					this.announcementId = attributes.announcement.id;
				} else {
					this.announcement = new Models.AnnouncementEntity(attributes.announcement);
					this.announcementId = this.announcement.id;
				}
			}
			if (attributes.scheduleId !== undefined) {
				this.scheduleId = attributes.scheduleId;
			}
			if (attributes.schedule !== undefined) {
				if (attributes.schedule === null) {
					this.schedule = attributes.schedule;
				} else if (attributes.schedule instanceof Models.ScheduleEntity) {
					this.schedule = attributes.schedule;
					this.scheduleId = attributes.schedule.id;
				} else {
					this.schedule = new Models.ScheduleEntity(attributes.schedule);
					this.scheduleId = this.schedule.id;
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		announcement {
			${Models.AnnouncementEntity.getAttributes().join('\n')}
			${Models.AnnouncementEntity.getFiles().map(f => f.name).join('\n')}
		}
		schedule {
			${Models.ScheduleEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return `${this.announcement.name} ${this.schedule.name}`;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AnnouncementTimeslotEntity.prototype, 'created');
CRUD(modifiedAttr)(AnnouncementTimeslotEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
